import React, { useEffect, useState } from 'react'
import DashboardLayout from '../../components/Admin/DashboardLayout'
import {
  MDBBtn,
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBIcon,
  MDBCollapse,
  MDBTooltip,
  MDBModalDialog,
  MDBModalContent,
} from 'mdb-react-ui-kit'
import PageHeading from '../../components/shared/PageHeading.component'
import EventsGrid from './components/EventsGrid/EventsGrid.component'
import EventStore from './store/Event.store'
import UserProfileService from '../../shared/services/UserProfile.service'
import moment from 'moment'

import './EventsPage.scss'
import { cleanHTML } from '../../shared/formatters/cleanHTML'

/**
 * Determine if text should be dark or light based on background color.
 * Uses relative luminance calculation.
 */
const getContrastTextColor = hexColor => {
  if (!hexColor) return '#ffffff'
  const hex = hexColor.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? '#1f2937' : '#ffffff'
}

/**
 * Calendar URL templates for adding events
 */
const GOOGLE_CAL_URL =
  'https://calendar.google.com/calendar/render?action=TEMPLATE&dates={startUtcZ}Z%2F{endUtcZ}Z&details={eventBody}&text={eventTitle}'
const OFFICE_365_URL =
  'https://outlook.office.com/calendar/0/action/compose?allday=false&enddt={endUtcIso}&path=%2Fcalendar%2Faction%2Fcompose&rru=addevent&startdt={startUtcIso}&subject={eventTitle}&body={eventBody}'

/**
 * Convert HTML to plain text for calendar descriptions
 */
const htmlToPlainText = html => {
  if (!html || typeof html !== 'string') return ''
  const div = document.createElement('div')
  div.innerHTML = html.replace(/<br\s*[/]?>/gi, '\n')
  return div.textContent || div.innerText || ''
}

/**
 * Process HTML description for safe rendering with working links.
 * Adds target="_blank" to links (except mailto) like EventViewer does.
 * Fixes URLs that are missing the https:// protocol.
 */
const processDescriptionHtml = html => {
  if (!html || typeof html !== 'string') return ''
  // Clean escaped quotes first
  let processed = cleanHTML(html)

  processed = processed
    .replace(/\\n/g, '<br />') // matches backslash + n from DB like "\\n"
    .replace(/\n/g, '<br />') // matches actual newline characters

  // Fix href URLs that start with www. but lack a protocol
  processed = processed.replace(/href=["']?(www\.)/gi, 'href="http://www.')
  // Add target="_blank" to links that are not mailto: (same as EventViewer)
  processed = processed.replace(/<a(?!.*?mailto:)/g, '<a target="_blank"')
  return processed
}

/**
 * Generate calendar URL for Google or Office 365
 */
const getCalendarUrl = (type, event) => {
  const startMoment = moment.utc(event.startsAt)
  const endMoment = moment.utc(event.endsAt)

  const params = {
    eventTitle: encodeURIComponent(event.summary || ''),
    eventBody: encodeURIComponent(htmlToPlainText(event.description || '')),
    startUtcZ: startMoment.format('YYYYMMDDTHHmmss'),
    endUtcZ: endMoment.format('YYYYMMDDTHHmmss'),
    startUtcIso: startMoment.format('YYYY-MM-DDTHH:mm:ss+00:00'),
    endUtcIso: endMoment.format('YYYY-MM-DDTHH:mm:ss+00:00'),
  }

  let url = type === 'google' ? GOOGLE_CAL_URL : OFFICE_365_URL
  Object.keys(params).forEach(key => {
    url = url.replace(`{${key}}`, params[key])
  })

  return url
}

/**
 * Download event as ICS file
 */
const downloadEventIcs = event => {
  const startMoment = moment.utc(event.startsAt)
  const endMoment = moment.utc(event.endsAt)

  const formatIcsDate = m => m.format('YYYYMMDDTHHmmss') + 'Z'
  const description = htmlToPlainText(event.description || '')
    .replace(/[\u0250-\ue007]/g, '')
    .substring(0, 500)

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'METHOD:REQUEST',
    'BEGIN:VEVENT',
    `UID:event-${event.id}@usabg-backoffice`,
    `DTSTAMP:${formatIcsDate(moment.utc())}`,
    `DTSTART:${formatIcsDate(startMoment)}`,
    `DTEND:${formatIcsDate(endMoment)}`,
    description ? `DESCRIPTION:${description}` : null,
    `SUMMARY:${event.summary || 'USABG Event'}`,
    `LAST-MODIFIED:${formatIcsDate(moment.utc())}`,
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n')

  const fileName = `${(event.summary || 'event')
    .trim()
    .replace(/ +/g, '-')
    .replace(/[^A-Za-z0-9-]/g, '')
    .substring(0, 24)}.${event.startDate}.ics`

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * EventDetailModal Component
 * Styled to match the training calendar event viewer modal
 */
const EventDetailModal = ({ isOpen, onClose, event, canDelete = false, onDelete }) => {
  if (!event) return null

  const {
    summary,
    description,
    eventColorHex,
    isMultiDay,
    displayStartDate,
    displayEndDate,
    displayStartTime,
    displayEndTime,
  } = event

  const handleAddToGoogle = () => {
    window.open(getCalendarUrl('google', event), '_blank')
  }

  const handleAddToOffice365 = () => {
    window.open(getCalendarUrl('office365', event), '_blank')
  }

  const handleDownloadIcs = () => {
    downloadEventIcs(event)
  }

  const handleDelete = () => {
    if (!canDelete || !onDelete) return
    if (window.confirm(`Remove "${summary}" from the USABG Events calendar?`)) {
      onDelete(event)
    }
  }

  const textColor = getContrastTextColor(eventColorHex)
  const headerStyle = eventColorHex ? { backgroundColor: eventColorHex, color: textColor } : {}

  return (
    <MDBModal open={isOpen} onClose={onClose}>
      <MDBModalDialog centered size='lg'>
        <MDBModalContent>
          <MDBModalHeader style={headerStyle} className={textColor === '#ffffff' ? 'text-white' : ''}>
            {summary}
            {canDelete && (
              <button type='button' className='delete-btn' onClick={handleDelete}>
                Delete Calendar Event
              </button>
            )}
            <MDBBtn type='button' className='btn-close' color='none' onClick={onClose}></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
            <div className='event-detail-content'>
              <div className='cal-summary'>
                <div className='cal-dates'>
                  <strong>Event Date & Time:</strong>
                  <div className='content'>
                    <div className='cal-date-row'>
                      <span className='cal-label'>Start Date:</span>
                      <span className='cal-time'>{displayStartTime}</span>
                      <span className='cal-date'>{displayStartDate}</span>
                    </div>
                    <div className='cal-date-row'>
                      <span className='cal-label'>End Date:</span>
                      <span className='cal-time'>{displayEndTime}</span>
                      <span className='cal-date'>{isMultiDay ? displayEndDate : displayStartDate}</span>
                    </div>
                  </div>
                </div>

                <div className='cal-add-btn'>
                  <div className='cal-add-header'>
                    <strong>Add to Your Calendar:</strong>
                    <MDBTooltip
                      tag='span'
                      wrapperClass='info-icon'
                      placement='bottom'
                      title={
                        <>
                          Office 365 Event will add to your Outlook calendar.
                          <br />
                          <br />
                          Google Event will add to your Google Calendar.
                          <br />
                          <br />
                          Download Event will save an .ics file to import.
                        </>
                      }
                    >
                      <MDBIcon icon='info-circle' />
                    </MDBTooltip>
                  </div>
                  <div className='content add-to-cal-buttons'>
                    <button className='cal-btn' onClick={handleAddToOffice365}>
                      <div className='cal-btn-icon'>
                        <MDBIcon fab icon='microsoft' />
                      </div>
                      <div className='cal-btn-text'>
                        <span>OFFICE 365</span>
                        <span>EVENT</span>
                      </div>
                    </button>
                    <button className='cal-btn' onClick={handleAddToGoogle}>
                      <div className='cal-btn-icon'>
                        <MDBIcon fab icon='google' />
                      </div>
                      <div className='cal-btn-text'>
                        <span>GOOGLE</span>
                        <span>EVENT</span>
                      </div>
                    </button>
                    <button className='cal-btn' onClick={handleDownloadIcs}>
                      <div className='cal-btn-icon'>
                        <MDBIcon icon='calendar-alt' />
                      </div>
                      <div className='cal-btn-text'>
                        <span>DOWNLOAD</span>
                        <span>EVENT</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {description && (
                <div className='cal-description'>
                  <strong>Event Description:</strong>
                  <div
                    className='content'
                    dangerouslySetInnerHTML={{
                      __html: processDescriptionHtml(description),
                    }}
                  />
                </div>
              )}

              <div className='event-footer'>
                <button className='close-btn' onClick={onClose}>
                  CLOSE
                </button>
              </div>
            </div>
          </MDBModalBody>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  )
}

/**
 * EventsPage - Without observer, using local state
 */
const EventsPage = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [pastEvents, setPastEvents] = useState([])

  useEffect(() => {
    const loadEvents = async () => {
      try {
        await EventStore.init()
        setUpcomingEvents(EventStore.upcomingEvents)
        setPastEvents(EventStore.pastEvents)
        setError(EventStore.error)
      } catch (err) {
        setError('Failed to load events')
      } finally {
        setIsLoading(false)
      }
    }
    loadEvents()
    return () => EventStore.reset()
  }, [])

  const handleEventClick = event => {
    setSelectedEvent(event)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedEvent(null)
  }

  const canDelete = UserProfileService.isA(['system-admin', 'internal-admin', 'internal-staff'])

  const handleDeleteEvent = async event => {
    try {
      await EventStore.deleteEvent(event.eventId)
      setUpcomingEvents(EventStore.upcomingEvents)
      setPastEvents(EventStore.pastEvents)
      closeModal()
    } catch (err) {
      setError('Failed to delete event. Please try again.')
    }
  }

  const toggleArchive = () => {
    setArchiveOpen(!archiveOpen)
  }

  return (
    <DashboardLayout>
      <main id='EventsPage' className='adminStyle mainSection pb-5'>
        <MDBContainer fluid className='mt-2'>
          <PageHeading label='USABG Events' />

          {error && (
            <div className='error-banner'>
              <MDBIcon icon='exclamation-triangle' />
              <span>{error}</span>
              <button onClick={() => EventStore.fetchEvents()}>Retry</button>
            </div>
          )}

          <MDBRow>
            <MDBCol size='12'>
              <EventsGrid
                events={upcomingEvents}
                onEventClick={handleEventClick}
                isLoading={isLoading}
                emptyMessage='No upcoming events scheduled.'
              />

              {pastEvents.length > 0 && (
                <div className='archive-section'>
                  <button className='archive-toggle' onClick={toggleArchive} aria-expanded={archiveOpen}>
                    <MDBIcon icon={archiveOpen ? 'chevron-down' : 'chevron-right'} />
                    <span>Past Events ({pastEvents.length})</span>
                  </button>
                  <MDBCollapse open={archiveOpen}>
                    <div className='archive-content'>
                      <EventsGrid
                        events={pastEvents}
                        onEventClick={handleEventClick}
                        isPast={true}
                        emptyMessage='No past events.'
                      />
                    </div>
                  </MDBCollapse>
                </div>
              )}
            </MDBCol>
          </MDBRow>
        </MDBContainer>

        <EventDetailModal
          isOpen={modalOpen}
          onClose={closeModal}
          event={selectedEvent}
          canDelete={canDelete}
          onDelete={handleDeleteEvent}
        />
      </main>
    </DashboardLayout>
  )
}

export default EventsPage
